import React, { useState, useEffect, useCallback, useMemo, useRef, type Ref, useImperativeHandle } from 'react';
import { Box, Container, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { componentService, ComponentData, shopService } from '@/services';
import { Shop, Gateway } from '@core/types';
import { DeleteConfirmationDialog } from '@/components';
import { DataTableColumn } from '@/components/datatable/WcnTable.tsx';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { ShopInformationRef } from '@features/administration/components/shopSetup/ShopInformation.tsx';
import NewWcnTable from '@/components/datatable/NewWcnTable.tsx';
import HardwareComponentEditor, {
    createEmptyComponent,
    HardwareComponentEditorRef
} from '@features/administration/components/shopSetup/HardwareComponentEditor.tsx';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { useRegisterDirtyGuard } from '@core/hooks/useRegisterDirtyGuard.ts';

const HardwareComponents: React.FC<{
    shop: Shop | null;
    loadingData: boolean;
    creating?: boolean;
    onCloseCreate?: (shopId?: string) => void;
    onEditing?: () => void;
    ref?: Ref<ShopInformationRef>;
}> = ({ shop, loadingData = false, creating = false, onCloseCreate, onEditing, ref }) => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess, notifyWarning } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [gateways, setGateways] = useState<Gateway[]>([]);
    const [components, setComponents] = useState<ComponentData[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [componentToDelete, setComponentToDelete] = useState<ComponentData | null>(null);
    const [selectedComponent, setSelectedComponent] = useState<ComponentData | null>(null);
    const editorRef = useRef<HardwareComponentEditorRef>(null);
    const editorSectionRef = useRef<HTMLDivElement>(null);

    const discardedRef = useRef(false);
    const prevCreatingRef = useRef(false);

    useEffect(() => {
        if (selectedComponent && shop && !!shop.gateways.length) {
            discardedRef.current = false;
            editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedComponent, shop]);

    useEffect(() => {
        if (creating && !prevCreatingRef.current && shop) {
            discardedRef.current = false;
            setSelectedComponent(createEmptyComponent(shop.id, shop.gateways.length ? 'SnmpDevice' : 'Gateway'));
        }
        prevCreatingRef.current = creating;
    }, [creating, shop]);

    useEffect(() => {
        if (selectedComponent) return;
        if (shop && shop.gateways.length === 0) {
            setSelectedComponent(createEmptyComponent(shop.id, 'Gateway'));
        }
    }, [shop]);

    useImperativeHandle(
        ref,
        () => ({
            get isDirty() {
                return !discardedRef.current && !!editorRef.current?.isDirty;
            },
            save: async () => !!(await editorRef.current?.save())
        }),
        [editorRef.current?.isDirty]
    );

    const handleCloseEditor = useCallback(
        (shouldReload = false) => {
            discardedRef.current = true;
            setSelectedComponent(null);
            onCloseCreate?.(shouldReload ? shop?.id : undefined);
        },
        [onCloseCreate, shop]
    );

    const save = useCallback(async () => {
        const saved = await editorRef.current?.save();
        if (saved) {
            handleCloseEditor(true);
        }
        return !!saved;
    }, [handleCloseEditor]);

    const discard = useCallback(() => {
        handleCloseEditor();
    }, [handleCloseEditor]);

    const isDirty = useCallback(() => !discardedRef.current && !!editorRef.current?.isDirty, []);
    const { guard } = useUnsavedChangesGuard({ isDirty, save });
    useRegisterDirtyGuard({ checkDirty: isDirty, save, discard });

    const handleEdit = useCallback(
        (component: ComponentData) => {
            guard(() => {
                discardedRef.current = false;
                onEditing?.();
                setSelectedComponent(structuredClone(component) as ComponentData);
            });
        },
        [guard, onEditing]
    );

    const handleCancelEditor = useCallback(() => {
        guard(() => handleCloseEditor());
    }, [guard, handleCloseEditor]);

    useEffect(() => {
        if (!shop) return;
        setGateways(shop.gateways);

        let rows = shop.gateways.map((g) => ({ ...g, name: g.deviceId, discriminator: 'Gateway' }) as any);
        if (shop.hybridLocStoreKey)
            rows = [
                ...rows,
                { name: 'HybridLoc', discriminator: 'HybridLoc', hybridLocStoreKey: shop.hybridLocStoreKey }
            ];
        if (shop.fastLanerGroupIds?.length)
            rows = [
                ...rows,
                { name: 'FastLaner', discriminator: 'FastLaner', fastLanerGroupIds: shop.fastLanerGroupIds }
            ];
        setComponents([...rows, ...(shop.components || [])]);
    }, [shop]);

    const handleDeleteClick = useCallback((component: ComponentData) => {
        if (component.discriminator === 'Gateway') {
            if (
                (shop?.components && shop.components.some((c) => c.gatewayId === component.gatewayId)) ||
                (component as any).modules?.length
            ) {
                notifyWarning(t('errors.gatewayInUse'));
                return;
            }
        }
        setComponentToDelete(component);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = async () => {
        if (!componentToDelete) return;

        setLoading(true);
        try {
            setDeleteDialogOpen(false);
            const { discriminator } = componentToDelete;

            if (discriminator === 'HybridLoc' || discriminator === 'FastLaner') {
                if (!shop) return;
                await shopService.saveShop({
                    ...shop,
                    ...(discriminator === 'HybridLoc' ? { hybridLocStoreKey: '' } : {}),
                    ...(discriminator === 'FastLaner' ? { fastLanerGroupIds: [] } : {})
                });
            } else {
                await componentService.deleteComponent(discriminator, componentToDelete.id || '');
            }
            notifySuccess();
            handleCloseEditor(true);
            setComponentToDelete(null);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const columns: DataTableColumn<ComponentData>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 380
            },
            {
                text: t('type'),
                value: 'discriminator',
                width: 180
            },
            {
                text: t('description'),
                value: 'description',
                width: 250
            },
            {
                text: t('gateway'),
                value: 'gateway',
                width: 200,
                render: (row) => {
                    const gatewayName = gateways.find((g) => g.id == row.gatewayId)?.deviceId;
                    return <Box sx={{ overflowY: 'auto' }}>{gatewayName}</Box>;
                }
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (row) => (
                    <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            title={t('edit')}
                        >
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(row);
                            }}
                            title={t('delete')}
                        >
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </Box>
                )
            }
        ],
        [t, gateways, handleEdit, handleDeleteClick]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', overflow: 'visible' }}>
            <Container maxWidth={false} sx={{ flex: 1, py: 3 }}>
                {!loading && components.length > 0 && (
                    <NewWcnTable
                        columns={columns}
                        data={components}
                        loading={loading}
                        noDataMessage={t('noData')}
                        size="small"
                    />
                )}

                {!loading && selectedComponent && shop && (
                    <Box ref={editorSectionRef} sx={{ mt: 3 }}>
                        <HardwareComponentEditor
                            key={selectedComponent.id}
                            component={selectedComponent}
                            shopId={shop.id}
                            shop={shop}
                            ref={editorRef}
                            onSave={() => handleCloseEditor(true)}
                            onCancel={handleCancelEditor}
                            onDelete={(c) => handleDeleteClick(c as ComponentData)}
                        />
                    </Box>
                )}
            </Container>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteComponentMessage', {
                    discriminator: componentToDelete?.discriminator,
                    name: componentToDelete?.name
                })}
                onConfirm={handleDeleteConfirm}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setComponentToDelete(null);
                }}
            />

            <LoadingOverlay open={loading || loadingData} />
        </Box>
    );
};

HardwareComponents.displayName = 'HardwareComponents Administration';
export default HardwareComponents;
